import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  EmptyBooking,
  EmptyInbox,
  EmptyNft,
  EmptyProperty,
  UnVerified,
  VerifyUserIcon,
} from "../../../AssetComponents/Images";
import { BlackButton } from "../../../Components/Buttons/BlackButton";
import { PurpleButton } from "../../../Components/Buttons/PurpleButton";
import { useSelector, useDispatch } from "react-redux";
import { removeNotificationById } from "../../../ReduxSlices/NotificationSlice";
import { RentalItem } from "../../../Components/RealEstateProperty/RentalItem";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import {
  getMyReservations,
  truncateWalletAddress,
  navigateToMessageNotificationInbox,
  resolveNftFromStore,
  isSameWalletAddress,
} from "../../../Components/functions/Functions";
import { getProfileFromWallet } from "../../../Components/functions/Functions";
import { UserBoxIcon } from "../../../AssetComponents/Images";
import { Fade } from "react-awesome-reveal";
import {
  SelectionGroup,
  SelectionItem,
} from "../../../Components/Selection/Selection";
import { LightIcon } from "../../../AssetComponents/Images";
import Tick from "../../../assets/images/Dashboard/Dashboard/Tick";
import { NotificationImageWithFallback } from "../../../Components/Images/ImageWithFallback";
import { PropertyImageWithFallback } from "../../../Components/Images/PropertyImageWithFallback";
import DraftsViewer from "../../../Components/Draft/DraftsViewer";
import { validatePropertyData } from "../../../utils/propertyValidation";
import { getRoleSpecificVerification } from "../../../utils/verificationUtils";
import { api } from "../../../Components/functions/Api";
import { toast } from "react-toastify";
import Modal from "react-responsive-modal";
import IcalSyncService from "../../../Services/IcalSyncService";

const formatDate = (timestamp, diff) => {
  try {
    const date = new Date((timestamp - diff) * 1000);
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }
    return new Intl.DateTimeFormat("en-US", {
      day: "numeric",
      month: "short",
    }).format(date);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
};

const formatTime = (timestamp, diff) => {
  try {
    const date = new Date((timestamp - diff) * 1000);
    if (isNaN(date.getTime())) {
      return 'Invalid time';
    }
    return new Intl.DateTimeFormat("en-US", {
      hour: "numeric", 
      minute: "2-digit",
      hour12: true,
    }).format(date);
  } catch (error) {
    console.error('Error formatting time:', error);
    return 'Invalid time';
  }
};

export const HostDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const currentProfile = useSelector((state) => state.auth.profile);
  const nfts = useSelector((state) => state.nft.nfts);
  const account = useSelector((state) => state.auth.account);
  // 🚀 OPTIMIZED: Remove duplicate selector - use only one diffToUTC
  const diffToUTC = useSelector((state) => state.time.diffToUTC);
  const notificationContent = useSelector((state) => state.notification.items);
  const [profiles, setProfiles] = useState({});
  const ref_nfts = useRef();
  const [flag, setFlag] = useState(0);
  const ref_requests = useRef();
  const [showDraftsModal, setShowDraftsModal] = useState(false);
  const [showAirbnbModal, setShowAirbnbModal] = useState(false);
  const [airbnbUrl, setAirbnbUrl] = useState("");
  const [isImporting, setIsImporting] = useState(false);
  const [isHydrating, setIsHydrating] = useState(true);
  const STATUS_MAP = ["upcoming", "current", "completed", "cancelled"];
  const [backendReservationsByStatus, setBackendReservationsByStatus] = useState({
    upcoming: [],
    current: [],
    completed: [],
    cancelled: [],
  });

  const normalizedAccount = useMemo(
    () => (account ? account.toLowerCase().trim() : null),
    [account]
  );

  // 🚀 OPTIMIZED: Use useMemo to prevent unnecessary re-renders
  const myNfts = useMemo(() => {
    const nfts_ = Object.values(nfts || {});
    const temp = [];
    for (let i = 0; i < nfts_.length; i++) {
      const currentNft = nfts_[i];
      const owner = currentNft?.access?.owner;
      if (owner?.toLowerCase() === normalizedAccount) {
        temp.push(currentNft);
      }
    }
    return temp;
  }, [nfts, normalizedAccount]);

  // Hydrate reservation data so host/dashboard matches /host/reservations.
  useEffect(() => {
    let cancelled = false;

    const hydrate = async () => {
      try {
        if (!account) return;
        // Fetch from backend directly so this dashboard doesn't depend on Redux NFT store shape/timing.
        const temp = {
          upcoming: [],
          current: [],
          completed: [],
          cancelled: [],
        };

        for (const status of STATUS_MAP) {
          const res = await getMyReservations(
            account,
            status,
            diffToUTC,
            undefined
          );
          temp[status] = res || [];
        }

        if (!cancelled) setBackendReservationsByStatus(temp);
      } catch (e) {
        console.error("HostDashboard: failed to hydrate reservations:", e);
      } finally {
        if (!cancelled) setIsHydrating(false);
      }
    };

    hydrate();
    return () => {
      cancelled = true;
    };
  }, [account, diffToUTC, dispatch]);

  // 🚀 OPTIMIZED: Memoize expensive reservation check result to prevent unnecessary re-renders
  const hasReservationsForFlag = useCallback((flag) => {
    const status = STATUS_MAP[flag] || "upcoming";
    return (backendReservationsByStatus?.[status] || []).length > 0;
  }, [backendReservationsByStatus]);

  const setPeriodParams = (token_id, from, to) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("from", from);
    searchParams.set("to", to);
    navigate(
      `/dashboard/host/reservations/${token_id}?${searchParams.toString()}`
    );
  };

  const getProfile = async (address, profiles) => {
    if (profiles[address] || profiles[address] === false) return;
    const profile = await getProfileFromWallet(address);
    profiles[address] = profile;
  };

  const parseAction = (action) => {
    switch (action) {
      case "finalize_short_term_rental":
        return (
          <div className="flex items-center">
            <div className="text-[#38A569]">Completed</div>
          </div>
        );
      case "set_reservation_for_short_term":
        return (
          <div className="flex items-center">
            <div className="text-[#38A569]">Reserved</div>
          </div>
        );
      case "cancel_reservation_for_shortterm":
        return (
          <div className="flex items-center">
            <div className="text-[#DB1F22]">Canceled</div>
          </div>
        );
      case "cancel_rental_for_shortterm":
        return (
          <div className="flex items-center">
            <div className="text-[#DB1F22]">Canceled</div>
          </div>
        );
      case "reject_reservation_for_shortterm":
        return (
          <div className="flex items-center">
            <div className="text-[#DB1F22]">Rejected</div>
          </div>
        );
      default:
        break;
    }
  };

  const getNotificationPath = (item, account, nfts) => {
    if (item.type === "message") {
      const nft = resolveNftFromStore(nfts, item.data?.nftId);
      const isPropertyOwner =
        nft?.access?.owner &&
        isSameWalletAddress(account, nft.access.owner);
      return isPropertyOwner
        ? `/dashboard/host/inbox?chat=${item.data.id}`
        : `/dashboard/traveler/inbox?chat=${item.data.id}`;
    }
    
    if (item.type === "event") {
      // Ensure token_id is valid
      const tokenId = item.data.token_id;
      if (!tokenId || tokenId === 'undefined' || tokenId === 'null') {
        return "/dashboard";
      }
      
      const nft = resolveNftFromStore(nfts, tokenId) || nfts[tokenId];
      const isOwner =
        nft?.access?.owner &&
        isSameWalletAddress(account, nft.access.owner);
      
      // Helper function to find renting_period from NFT data
      const getRentingPeriod = () => {
        // First, try to get from notification data directly
        if (item.data.renting_period && Array.isArray(item.data.renting_period) && item.data.renting_period.length === 2) {
          return item.data.renting_period;
        }
        
        // Otherwise, try to find from NFT rentals array
        if (nft?.rentals?.rentals && Array.isArray(nft.rentals.rentals)) {
          const action = item.data.action;
          const sender = item.data.sender;
          
          // For cancel/reject actions, find the cancelled/rejected reservation
          if (action === "cancel_reservation_for_shortterm" || 
              action === "cancel_rental_for_shortterm" || 
              action === "reject_reservation_for_shortterm") {
            // Find the most recent cancelled/rejected reservation
            for (let i = nft.rentals.rentals.length - 1; i >= 0; i--) {
              const reservation = nft.rentals.rentals[i];
              if (reservation.renting_period && 
                  Array.isArray(reservation.renting_period) && 
                  reservation.renting_period.length === 2 &&
                  (reservation.cancelled || (!reservation.approved && action === "reject_reservation_for_shortterm"))) {
                return reservation.renting_period;
              }
            }
          }
          
          // For finalize action, find the most recent finalized (approved) reservation
          if (action === "finalize_short_term_rental") {
            for (let i = nft.rentals.rentals.length - 1; i >= 0; i--) {
              const reservation = nft.rentals.rentals[i];
              if (reservation.renting_period && 
                  Array.isArray(reservation.renting_period) && 
                  reservation.renting_period.length === 2 &&
                  reservation.approved && 
                  !reservation.cancelled) {
                return reservation.renting_period;
              }
            }
          }
          
          // For set_reservation, find the most recent reservation that matches the sender
          if (action === "set_reservation_for_short_term") {
            for (let i = nft.rentals.rentals.length - 1; i >= 0; i--) {
              const reservation = nft.rentals.rentals[i];
              if (reservation.renting_period && 
                  Array.isArray(reservation.renting_period) && 
                  reservation.renting_period.length === 2 &&
                  reservation.address === sender) {
                return reservation.renting_period;
              }
            }
          }
          
          // Fallback: get the most recent reservation with valid renting_period
          for (let i = nft.rentals.rentals.length - 1; i >= 0; i--) {
            const reservation = nft.rentals.rentals[i];
            if (reservation.renting_period && 
                Array.isArray(reservation.renting_period) && 
                reservation.renting_period.length === 2) {
              return reservation.renting_period;
            }
          }
        }
        
        return null;
      };
      
      switch (item.data.action) {
        case "set_reservation_for_short_term":
        case "finalize_short_term_rental":
        case "cancel_reservation_for_shortterm":
        case "cancel_rental_for_shortterm":
        case "reject_reservation_for_shortterm":
          const rentingPeriod = getRentingPeriod();
          if (rentingPeriod && rentingPeriod.length === 2) {
            const from = rentingPeriod[0];
            const to = rentingPeriod[1];
            return isOwner 
              ? `/dashboard/host/reservations/${tokenId}?from=${from}&to=${to}` 
              : `/dashboard/traveler/trips/${tokenId}?from=${from}&to=${to}`;
          }
          
          // Fallback to reservations with token_id if we can't find renting_period
          return isOwner 
            ? `/dashboard/host/reservations/${tokenId}` 
            : `/dashboard/traveler/trips/${tokenId}`;
        
        default:
          // For any other event with a token_id, redirect to reservations
          return isOwner 
            ? `/dashboard/host/reservations/${tokenId}` 
            : `/dashboard/traveler/trips/${tokenId}`;
      }
    }
    
    return "/dashboard";
  };

  // 🚀 OPTIMIZED: Memoize profile addresses to prevent unnecessary re-fetching
  const profileAddressesKey = useMemo(() => {
    const addresses = new Set();
    for (let i = 0; i < notificationContent.length; i++) {
      if (notificationContent[i].type === "message") {
        addresses.add(notificationContent[i].data.message.sender);
      } else {
        addresses.add(notificationContent[i].data.sender);
      }
    }
    return Array.from(addresses).sort().join(','); // Sort for consistent key
  }, [notificationContent]);

  const getProfiles = useCallback(async () => {
    // Only update if addresses have changed
    const addresses = profileAddressesKey.split(',').filter(Boolean);
    let tempProfiles = { ...profiles };
    let hasChanges = false;
    
    for (const address of addresses) {
      if (!tempProfiles[address] && tempProfiles[address] !== false) {
        hasChanges = true;
        const profile = await getProfileFromWallet(address);
        tempProfiles[address] = profile;
      }
    }
    
    if (hasChanges) {
      setProfiles(tempProfiles);
    }
  }, [profileAddressesKey, profiles]);

  // 🚀 OPTIMIZED: Only fetch profiles when addresses change, not on every notification update
  useEffect(() => {
    if (profileAddressesKey) {
      getProfiles();
    }
  }, [profileAddressesKey]); // Only depend on address key string

  // Check if host is verified (requires BOTH KYC and Emirates ID)
  const hostVerification = useMemo(() => {
    const verification = getRoleSpecificVerification(currentProfile, 'host');
    console.log("🔍 [RECLAIM] Host verification status checked:");
    console.log("   → Is Verified:", verification.isVerified);
    console.log("   → Verification Level:", verification.verificationLevel);
    console.log("   → Verification Method:", verification.verificationMethod);
    console.log("   → Verification Text:", verification.verificationText);
    console.log("   → Has KYC:", currentProfile?.verifications?.kyc?.verified || false);
    console.log("   → Has Emirates ID:", currentProfile?.verifications?.emiratesId?.verified || false);
    console.log("   → KYC Verified At:", currentProfile?.verifications?.kyc?.verifiedAt || "N/A");
    console.log("   → Emirates ID Verified At:", currentProfile?.verifications?.emiratesId?.verifiedAt || "N/A");
    console.log("   → Verification Source:", currentProfile?.verifications?.emiratesId?.verified ? "Reclaim Protocol" : "N/A");
    return verification;
  }, [currentProfile]);

  // Handle verification check and redirect
  const checkVerificationAndRedirect = (action) => {
    console.log("🔍 [RECLAIM] Checking verification for action:", action);
    console.log("   → Host verification status:", hostVerification.isVerified);
    console.log("   → Has KYC:", hostVerification.hasKyc || false);
    console.log("   → Has Emirates ID:", hostVerification.hasEmiratesId || false);
    
    if (!hostVerification.isVerified) {
      console.log("⚠️ [RECLAIM] Host not verified - redirecting to profile");
      
      // Provide specific message based on what's missing
      const hasKyc = hostVerification.hasKyc || false;
      const hasEmiratesId = hostVerification.hasEmiratesId || false;
      
      let message = "Please complete host verification to use this feature";
      if (!hasKyc && !hasEmiratesId) {
        message = "Please verify your identity with KYC first, then Emirates ID via Reclaim Protocol to use this feature";
      } else if (hasKyc && !hasEmiratesId) {
        message = "Please complete Emirates ID verification via Reclaim Protocol to use this feature";
      } else if (!hasKyc && hasEmiratesId) {
        message = "Please complete KYC verification first, then Emirates ID via Reclaim Protocol to use this feature";
      }
      
      toast.warning(message, {
        autoClose: 5000,
      });
      navigate("/dashboard/profile?mode=host");
      return false;
    }
    console.log("✅ [RECLAIM] Host verified (KYC + Emirates ID) - allowing action");
    return true;
  };

  // Handle Airbnb import
  const handleImportFromAirbnb = async () => {
    // Check verification first
    if (!checkVerificationAndRedirect("import from Airbnb")) {
      return;
    }

    if (!airbnbUrl || !airbnbUrl.trim()) {
      toast.error("Please enter an Airbnb URL");
      return;
    }

    // Validate URL format
    const urlPattern = /^https?:\/\/(www\.)?airbnb\.(com|co\.in|co\.uk|ca|au|de|fr|es|it|nl|be|at|ch|pl|cz|se|no|dk|fi|ie|pt|gr|hu|ro|bg|hr|sk|si|lt|lv|ee|lu|mt|cy|is|li|ad|mc|sm|va|jp|kr|sg|hk|tw|my|th|ph|id|vn|in|nz|za|mx|br|ar|cl|co|pe|ec|uy|py|bo|ve|cr|pa|gt|hn|ni|sv|do|pr|jm|tt|bs|aw|ky|vg|ag|lc|vc|kn|dm|gd|bb|bz|sr|gy|gf|mq|gp|re|pf|nc|vu|fj|pg|sb|to|ws|as|gu|mp|fm|pw|mh|ki|tv|nr|nu|ck|pn|gs|tf|hm|nf|aq)/i;
    
    if (!urlPattern.test(airbnbUrl.trim())) {
      toast.error("Please enter a valid Airbnb URL");
      return;
    }

    if (!airbnbUrl.includes('/rooms/')) {
      toast.error("URL must be a valid Airbnb listing (must contain /rooms/)");
      return;
    }

    setIsImporting(true);
    try {
      const response = await api("property/import-from-airbnb", {
        airbnb_url: airbnbUrl.trim()
      });

      if (response.success && response.data) {
        // Also scrape blocked dates from the Airbnb URL (without saving to DB yet)
        let blockedDatesData = null;
        try {
          console.log("📅 [Airbnb Import] Scraping blocked dates from Airbnb URL...");
          const blockedDatesResponse = await IcalSyncService.getScrapedBlockedDates(airbnbUrl.trim(), null);
          
          if (blockedDatesResponse.success && blockedDatesResponse.scrapeResult) {
            blockedDatesData = {
              url: airbnbUrl.trim(),
              blockedDates: blockedDatesResponse.scrapeResult.blockedDates || [],
              dateRanges: blockedDatesResponse.scrapeResult.dateRanges || [],
              totalBlockedDates: blockedDatesResponse.scrapeResult.totalBlockedDates || 0,
              totalRanges: blockedDatesResponse.scrapeResult.totalRanges || 0
            };
            console.log(`✅ [Airbnb Import] Found ${blockedDatesData.totalBlockedDates} blocked dates (${blockedDatesData.totalRanges} ranges)`);
          }
        } catch (blockedDatesError) {
          console.warn("⚠️ [Airbnb Import] Failed to scrape blocked dates:", blockedDatesError);
          // Don't fail the whole import if blocked dates scraping fails
        }
        
        // Store the scraped data in sessionStorage (including blocked dates)
        const dataToStore = {
          ...response.data,
          blockedDates: blockedDatesData // Store blocked dates data
        };
        sessionStorage.setItem('airbnbImportedData', JSON.stringify(dataToStore));
        
        // Show warnings if any
        if (response.scrapingInfo?.warnings?.length > 0) {
          response.scrapingInfo.warnings.forEach(warning => {
            toast.warning(warning, { autoClose: 4000 });
          });
        }

        const successMessage = blockedDatesData 
          ? `Property imported successfully! Found ${blockedDatesData.totalBlockedDates} blocked dates. Redirecting to minting page...`
          : "Property imported successfully! Redirecting to minting page...";
        toast.success(successMessage);
        
        // Close modal and navigate to mint page with airbnb param
        setShowAirbnbModal(false);
        setAirbnbUrl("");
        
        // Small delay to show success message
        setTimeout(() => {
          navigate("/dashboard/host/mint?airbnb=true");
        }, 1000);
      } else {
        toast.error(response.error || "Failed to import property");
      }
    } catch (error) {
      console.error("Airbnb import error:", error);
      const errorMessage = error.response?.data?.error || error.message || "Failed to import from Airbnb";
      toast.error(errorMessage);
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="select-none flex-1 min-w-0 max-h-[calc(100vh-80px)] h-full flex flex-col dashboard-container dashboard-no-scrollbars">
      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-[6px] sm:p-[10px]">
        <div className="w-full space-y-[12px] sm:space-y-[20px] rounded-[10px] p-[4px] sm:p-[8px] flex flex-col dashboard-content">
        
        {/* Welcome Header Section */}
        <div className="bg-white border border-[#E3E3E3] rounded-[10px] p-6 shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] dashboard-card m-[6px] sm:m-[10px]">
          <div className="font-semibold text-2xl">Dashboard</div>
          <div className="text-[#666666] font-normal text-base mt-2">
            Welcome to the Coded Estate Dashboard 
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="lg:hidden space-y-[12px] sm:space-y-[20px] w-full overflow-x-hidden">
          {/* Verify Apartments Section */}
          <div className="w-full flex flex-col space-y-[8px] sm:space-y-[10px]">
            <div className="font-semibold text-sm sm:text-base">Verify Apartments</div>
            <div className="bg-white flex flex-col justify-around p-2 items-center border-[2px] h-[200px] sm:h-[240px] w-full border-[#E3E3E3] rounded-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
              {currentProfile?.Avatar ? (
                <img
                  alt=""
                  src={currentProfile?.Avatar}
                  className="w-[60px] sm:w-[80px] rounded-full shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]"
                />
              ) : (
                <div className="scale-75 sm:scale-100">
                  <VerifyUserIcon />
                </div>
              )}
              {currentProfile?.Name ? (
                <div className="font-semibold text-sm sm:text-base text-center">
                  {currentProfile?.Name?.replace("/", " ")}
                </div>
              ) : (
                <div className="font-semibold text-sm sm:text-base">Unknown</div>
              )}

              {(() => {
                const hostVerification = getRoleSpecificVerification(currentProfile, 'host');
                return hostVerification.isVerified ? (
                  <div className="flex items-center gap-1 sm:gap-2">
                    <div className="scale-75 sm:scale-100">
                      <Tick/>
                    </div>
                    <div className="text-[#959595] font-normal text-[12px] sm:text-[14px]">
                      {hostVerification.verificationText}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-[8px] sm:gap-[10px]">
                    <div className="scale-75 sm:scale-100">
                      <UnVerified />
                    </div>
                    <div className="text-[#959595] font-normal text-[12px] sm:text-[14px]">
                      {hostVerification.verificationText}
                    </div>
                  </div>
                );
              })()}
              {(() => {
                const hostVerification = getRoleSpecificVerification(currentProfile, 'host');
                return hostVerification.isVerified ? (
                  <div className="w-full">
                    <PurpleButton
                      text={
                        <div
                          onClick={() => navigate("/dashboard/profile?mode=host")}
                          className="w-full text-center text-sm sm:text-base"
                        >
                          Your Profile
                        </div>
                      }
                    />
                  </div>
                ) : (
                  <div className="w-full">
                    <BlackButton
                      text={
                        <div
                          onClick={() => navigate("/dashboard/profile?mode=host")}
                          className="w-full text-center text-sm sm:text-base"
                        >
                          Verify now
                        </div>
                      }
                    />
                  </div>
                );
              })()}
            </div>
          </div>

                     {/* Your Minted Apartments Section */}
           <div className="w-full flex flex-col space-y-[8px] sm:space-y-[10px]">
             <div className="font-semibold text-sm sm:text-base">Your Minted Apartments</div>
             <div className="relative w-full h-[320px] sm:h-[280px] bg-white p-2 border-[2px] border-[#E3E3E3] rounded-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] overflow-hidden">
              <div
                ref={ref_nfts}
                className="pb-1 flex gap-[8px] sm:gap-[10px] items-center overflow-x-auto overflow-y-hidden scrollbar-hide w-full h-full"
              >
                {myNfts.map((nft, i) => {
                  const validatedNft = validatePropertyData(nft);
                  return (
                    <Fade key={i}>
                      <div className="w-max h-max relative group flex-shrink-0">
                        <RentalItem
                          showArrow={false}
                          token_id={validatedNft.token_id}
                          tokenData={validatedNft}
                          imageHeight={window.innerWidth < 640 ? 160 : 200}
                          onlyImages
                          hideWishlist={true}
                        />

                        <div className="group-hover:flex absolute top-0 left-0 w-full h-full hidden flex-col items-center justify-center">
                          <BlackButton
                            text="View"
                            onClick={() =>
                              navigate(
                                `/dashboard/host/nfts/${validatedNft.token_id}`
                              )
                            }
                          />
                        </div>
                      </div>
                    </Fade>
                  );
                })}
              </div>

              {myNfts.length === 0 && (
                <div className="absolute inset-0 flex flex-col justify-center items-center text-center p-4">
                  <div className="scale-75 sm:scale-100">
                    <EmptyProperty />
                  </div>
                  <div className="font-semibold text-sm sm:text-base mt-2">
                    You have no properties yet
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Booking Request Section */}
          <div className="w-full flex flex-col space-y-[12px] sm:space-y-[16px]">
            <div className="font-semibold text-sm sm:text-base">Booking request</div>

            <div
              ref={ref_requests}
              className="border-[2px] border-[#E3E3E3] h-[320px] sm:h-[280px] bg-white p-[12px] sm:p-[16px] overflow-auto w-full rounded-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]"
            >
              <div className="w-full flex flex-col sm:flex-row gap-[16px] sm:gap-[20px] justify-between items-start sm:items-center mb-[16px] sm:mb-[20px]">
                <SelectionGroup
                  defaultItem={flag}
                  className="border-[2px] border-[#e3e3e3] w-full sm:w-max px-[8px] sm:px-[12px] py-[6px] sm:py-[8px] gap-[4px] sm:gap-[12px] flex items-center rounded-[14px] bg-white shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]"
                >
                  <SelectionItem
                    SelectedItem={
                      <div className="py-[6px] sm:py-[8px] cursor-pointer bg-white rounded-[10px] w-full sm:w-[120px] flex justify-center gap-[4px] sm:gap-[12px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
                        <div className="text-black font-semibold text-[11px] sm:text-sm">Upcoming</div>
                        <div className="scale-75 sm:scale-100">
                          <LightIcon />
                        </div>
                      </div>
                    }
                    UnselectedItem={
                      <div
                        onClick={() => setFlag(0)}
                        className="py-[6px] sm:py-[8px] cursor-pointer hover:bg-[#f6f6f6] rounded-[10px] w-full sm:w-[120px] flex justify-center"
                      >
                        <div className="text-[#959595] text-[11px] sm:text-sm">Upcoming</div>
                      </div>
                    }
                  />
                  <SelectionItem
                    SelectedItem={
                      <div className="py-[6px] sm:py-[8px] cursor-pointer bg-white rounded-[10px] w-full sm:w-[120px] flex justify-center gap-[4px] sm:gap-[12px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
                        <div className="text-black font-semibold text-[11px] sm:text-sm">Current</div>
                        <div className="scale-75 sm:scale-100">
                          <LightIcon />
                        </div>
                      </div>
                    }
                    UnselectedItem={
                      <div
                        onClick={() => setFlag(1)}
                        className="py-[6px] sm:py-[8px] cursor-pointer hover:bg-[#f6f6f6] rounded-[10px] w-full sm:w-[120px] flex justify-center"
                      >
                        <div className="text-[#959595] text-[11px] sm:text-sm">Current</div>
                      </div>
                    }
                  />
                  <SelectionItem
                    SelectedItem={
                      <div className="py-[6px] sm:py-[8px] cursor-pointer bg-white rounded-[10px] w-full sm:w-[120px] flex justify-center gap-[4px] sm:gap-[12px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
                        <div className="text-black font-semibold text-[11px] sm:text-sm">Completed</div>
                        <div className="scale-75 sm:scale-100">
                          <LightIcon />
                        </div>
                      </div>
                    }
                    UnselectedItem={
                      <div
                        onClick={() => setFlag(2)}
                        className="py-[6px] sm:py-[8px] cursor-pointer hover:bg-[#f6f6f6] rounded-[10px] w-full sm:w-[120px] flex justify-center"
                      >
                        <div className="text-[#959595] text-[11px] sm:text-sm">Completed</div>
                      </div>
                    }
                  />
                  <SelectionItem
                    SelectedItem={
                      <div className="py-[6px] sm:py-[8px] cursor-pointer bg-white rounded-[10px] w-full sm:w-[120px] flex justify-center gap-[4px] sm:gap-[12px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
                        <div className="text-black font-semibold text-[11px] sm:text-sm">Cancelled</div>
                        <div className="scale-75 sm:scale-100">
                          <LightIcon />
                        </div>
                      </div>
                    }
                    UnselectedItem={
                      <div
                        onClick={() => setFlag(3)}
                        className="py-[6px] sm:py-[8px] cursor-pointer hover:bg-[#f6f6f6] rounded-[10px] w-full sm:w-[120px] flex justify-center"
                      >
                        <div className="text-[#959595] text-[11px] sm:text-sm">Cancelled</div>
                      </div>
                    }
                  />
                </SelectionGroup>
              </div>
              
              <div className="flex gap-[12px] sm:gap-[16px] items-center mt-[8px] sm:mt-[12px] pb-[8px] sm:pb-[12px] w-full overflow-x-auto scrollbar-hide">
                {isHydrating ? (
                  <div className="flex flex-col items-center justify-center w-full h-full py-[20px] sm:py-[32px]">
                    <div className="text-sm sm:text-base">Loading reservations...</div>
                  </div>
                ) : (
                  (() => {
                    // 🚀 OPTIMIZED: Memoize the result to prevent re-computation on every render
                    const status = STATUS_MAP[flag] || "upcoming";
                    const matchingReservations =
                      backendReservationsByStatus?.[status] || [];
                    return matchingReservations.length ? (
                      matchingReservations.map(
                        ({ nft, reservation, key }, idx) => {
                          const period = reservation?.renting_period;
                          const start = Array.isArray(period) ? period[0] : 0;
                          const end = Array.isArray(period) ? period[1] : 0;
                          const validatedNft = validatePropertyData(nft);

                          return (
                            <Fade
                              key={
                                key ??
                                `${validatedNft.token_id}-${reservation?.address}-${start}-${end}-${idx}`
                              }
                            >
                              <div className="w-max h-max relative group flex-shrink-0">
                                <RentalItem
                                  traveler={reservation?.address}
                                  rentingPeriod={
                                    Array.isArray(period) ? period : undefined
                                  }
                                  token_id={validatedNft.token_id}
                                  tokenData={validatedNft}
                                  onlyImages
                                  imageHeight={
                                    window.innerWidth < 640 ? 120 : 153
                                  }
                                  hideWishlist={true}
                                />
                                <div className="group-hover:flex absolute top-0 left-0 w-full h-full hidden flex-col items-center justify-center">
                                  <BlackButton
                                    text="View all"
                                    onClick={() =>
                                      setPeriodParams(
                                        validatedNft.token_id,
                                        start,
                                        end
                                      )
                                    }
                                  />
                                </div>
                              </div>
                            </Fade>
                          );
                        }
                      )
                    ) : (
                      <div className="flex flex-col items-center w-full justify-center h-full py-[20px] sm:py-[32px]">
                        <div className="scale-75 sm:scale-100">
                          <EmptyBooking />
                        </div>
                        <div className="text-sm sm:text-base mt-[12px] sm:mt-[16px]">
                          No reservations yet
                        </div>
                        <div className="text-[#737373] text-xs sm:text-sm mt-[4px] sm:mt-[8px]">
                          {flag === 0 && "No upcoming reservations yet"}
                          {flag === 1 && "No current reservations yet"}
                          {flag === 2 && "No completed reservations yet"}
                          {flag === 3 && "No cancelled reservations yet"}
                        </div>
                      </div>
                    );
                  })()
                )}
              </div>
            </div>
          </div>

          {/* Mint Your Real Estate Apartment Section */}
          <div className="w-full flex flex-col space-y-4">
            <div className="font-semibold text-base">Mint Your Real Estate Apartment</div>
            <div className="bg-white flex flex-col justify-around items-center border-[2px] border-[#E3E3E3] rounded-[10px] p-6 shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] w-full h-[300px] sm:h-[400px]">
              <div className="scale-75 sm:scale-100">
                <EmptyNft />
              </div>
              {myNfts.length === 0 && (
                <div className="flex flex-col items-center text-center">
                  <div className="font-semibold text-sm sm:text-base">No Apartments minted yet</div>
                  <div className="text-[#959595] font-normal text-[12px] sm:text-[14px]">
                    Your Apartment minted is empty yet
                  </div>
                </div>
              )}

              <div className="w-full flex flex-col gap-3">
                <PurpleButton
                  text={
                    <div
                      onClick={() => {
                        if (!checkVerificationAndRedirect("create property")) {
                          return;
                        }
                        navigate("/dashboard/host/mint");
                      }}
                      className="min-w-[100px] text-center text-sm sm:text-base"
                    >
                      Create New Property
                    </div>
                  }
                />
                <PurpleButton
                  text={
                    <div
                      onClick={() => {
                        if (!checkVerificationAndRedirect("import from Airbnb")) {
                          return;
                        }
                        setShowAirbnbModal(true);
                      }}
                      className="min-w-[100px] text-center text-sm sm:text-base"
                    >
                      Import from Airbnb
                    </div>
                  }
                />
                <BlackButton
                  text={
                    <div
                      onClick={() => {
                        if (!checkVerificationAndRedirect("view drafts")) {
                          return;
                        }
                        setShowDraftsModal(true);
                      }}
                      className="min-w-[100px] text-center text-sm sm:text-base"
                    >
                      View Drafts
                    </div>
                  }
                />
              </div>
            </div>
          </div>

          {/* Notifications Section */}
          <div className="w-full flex flex-col space-y-[8px] sm:space-y-[10px]">
            <div className="font-semibold text-sm sm:text-base">Notifications</div>

            {notificationContent.length > 0 ? (
              <div className="bg-white space-y-[3px] sm:space-y-[4px] border-[2px] h-[250px] sm:h-[300px] max-h-[250px] sm:max-h-[300px] w-full overflow-y-auto overflow-x-hidden border-[#E3E3E3] p-[6px] sm:p-[8px] rounded-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
                {notificationContent
                  .slice()
                  .reverse()
                  .map((item, i) => {
                    if (item.type === "event")
                      return (
                        <div
                          key={`event-${i}`}
                          onClick={() => {
                            // Remove notification when clicked
                            dispatch(removeNotificationById({
                              type: "event",
                              identifier: {
                                token_id: item.data.token_id,
                                sender: item.data.sender,
                                action: item.data.action,
                                txHash: item.data.txHash,
                              }
                            }));
                            const path = getNotificationPath(item, account, nfts);
                            navigate(path);
                          }}
                          className="hover:bg-[#f0f0f0] rounded-[4px] cursor-pointer p-[4px] sm:p-[6px] w-full flex items-center justify-between"
                        >
                          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                            <NotificationImageWithFallback
                              src={profiles[item.data.sender]?.Avatar}
                              alt="User Avatar"
                              className="shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] flex-shrink-0"
                              size="lg"
                            />

                            <div className="min-w-0 flex-1">
                              <div className="text-xs sm:text-base truncate">
                                {profiles[item.data.sender]?.Name
                                  ? profiles[item.data.sender]?.Name?.replace(
                                      "/",
                                      " "
                                    )
                                  : truncateWalletAddress(item.data.sender)}
                              </div>
                              <div className="flex gap-1 sm:gap-2 min-w-0 flex-1 truncate">
                                <span className="font-semibold text-xs sm:text-base">
                                  {parseAction(item.data.action)}
                                </span>
                                <span className="truncate text-xs sm:text-base">
                                  {nfts?.[item.data.token_id]?.metaData?.buildingName || ''}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
                            <PropertyImageWithFallback
                              src={nfts?.[item.data.token_id]?.metaData?.images?.[0]}
                              alt="Property"
                              className="w-[30px] sm:w-[60px] h-[20px] sm:h-full rounded-[4px]"
                            />
                            <div className="text-right">
                              <div className="text-[10px] sm:text-[12px]">
                                {formatDate(
                                  Math.round(new Date(item.data.timeUTC).getTime() / 1000),
                                  diffToUTC
                                )}
                              </div>
                              <div className="text-[10px] sm:text-[12px]">
                                {formatTime(
                                  Math.round(new Date(item.data.timeUTC).getTime() / 1000),
                                  diffToUTC
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    else
                      return (
                        <div
                          key={`message-${i}`}
                          onClick={async () => {
                            // Remove notification when clicked
                            dispatch(removeNotificationById({
                              type: "message",
                              identifier: {
                                chatId: item.data.id,
                              }
                            }));
                            await navigateToMessageNotificationInbox({
                              item,
                              account,
                              nfts,
                              pathname: location.pathname,
                              navigate,
                            });
                          }}
                          className="hover:bg-[#f0f0f0] rounded-[4px] cursor-pointer p-[4px] sm:p-[6px] w-full flex items-center justify-between"
                        >
                          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                            {profiles[item.data.message.sender]?.Avatar ? (
                              <img
                                alt=""
                                src={profiles[item.data.message.sender]?.Avatar}
                                className="w-[36px] h-[36px] sm:w-[44px] sm:h-[44px] rounded-full shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] flex-shrink-0"
                              />
                            ) : (
                              <div className="w-[36px] h-[36px] sm:w-[44px] sm:h-[44px] flex bg-[#f0f0f0] rounded-full flex-shrink-0">
                                <div className="scale-75 sm:scale-100 m-auto">
                                  <UserBoxIcon />
                                </div>
                              </div>
                            )}

                            <div className="min-w-0 flex-1">
                              <div className="text-xs sm:text-base truncate">
                                {profiles[item.data.message.sender]?.Name
                                  ? profiles[
                                      item.data.message.sender
                                    ]?.Name?.replace("/", " ")
                                  : truncateWalletAddress(
                                      item.data.message.sender
                                    )}
                              </div>

                              <div className="flex gap-1 sm:gap-2 min-w-0 flex-1 truncate">
                                <span className="truncate text-xs sm:text-base">
                                  Sent new message
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
                            <PropertyImageWithFallback
                              src={nfts?.[item.data.nftId]?.metaData?.images?.[0]}
                              alt="Property"
                              className="w-[30px] sm:w-[60px] h-[20px] sm:h-full rounded-[4px]"
                            />
                            <div className="text-right">
                              <div className="text-[10px] sm:text-[12px]">
                                {formatDate(
                                  Math.round(new Date(item.data.message.generatedTime).getTime() / 1000),
                                  diffToUTC
                                )}
                              </div>
                              <div className="text-[10px] sm:text-[12px]">
                                {formatTime(
                                  Math.round(new Date(item.data.message.generatedTime).getTime() / 1000),
                                  diffToUTC
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                  })}
              </div>
            ) : (
              <div className="bg-white flex flex-col justify-around items-center border-[2px] h-[220px] sm:h-[300px] w-full border-[#E3E3E3] p-[16px] sm:p-[24px] rounded-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
                <div className="scale-75 sm:scale-100">
                  <EmptyInbox />
                </div>
                <div className="flex flex-col items-center text-center">
                  <div className="font-semibold text-sm sm:text-base">No notifications yet</div>
                  <div className="text-[#959595] font-normal text-[12px] sm:text-[14px]">
                    Your inbox is empty yet
                  </div>
                </div>
                <PurpleButton
                  text={<div className="min-w-[100px] text-center text-sm sm:text-base">Inbox</div>}
                  onClick={() => navigate("/dashboard/host/inbox")}
                />
              </div>
            )}
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden lg:flex flex-row gap-6 w-full">
          {/* Verify Apartments Section - Figma: width: 265px, height: 265px */}
          <div className="flex flex-col space-y-4 flex-1 min-w-0">
            <div className="font-semibold text-base">Verify Apartments</div>
            <div className="bg-white flex flex-col justify-around items-center border border-[#E3E3E3] rounded-[10px] p-6 shadow flex-1 min-h-[265px]">
            {currentProfile?.Avatar ? (
              <img
                alt=""
                src={currentProfile?.Avatar}
                className="w-[60px] sm:w-[80px] rounded-full shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]"
              />
            ) : (
              <div className="scale-100">
                <VerifyUserIcon />
              </div>
            )}
            {currentProfile?.Name ? (
              <div className="font-semibold text-base text-center">
                {currentProfile?.Name?.replace("/", " ")}
              </div>
            ) : (
              <div className="font-semibold text-base">Unknown</div>
            )}

            {(() => {
              const hostVerification = getRoleSpecificVerification(currentProfile, 'host');
              return hostVerification.isVerified ? (
                <div className="flex items-center gap-2">
                  <div className="scale-100">
                    <Tick/>
                  </div>
                  <div className="text-[#959595] font-normal text-sm">
                    {hostVerification.verificationText}
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="scale-100">
                    <UnVerified />
                  </div>
                  <div className="text-[#959595] font-normal text-sm">
                    {hostVerification.verificationText}
                  </div>
                </div>
              );
            })()}
            {(() => {
              const hostVerification = getRoleSpecificVerification(currentProfile, 'host');
              return hostVerification.isVerified ? (
                <div className="w-full">
                  <PurpleButton
                    text={
                      <div
                        onClick={() => navigate("/dashboard/profile?mode=host")}
                        className="w-full text-center text-base"
                      >
                        Your Profile
                      </div>
                    }
                  />
                </div>
              ) : (
                <div className="w-full">
                  <BlackButton
                    text={
                      <div
                        onClick={() => navigate("/dashboard/profile?mode=host")}
                        className="w-full text-center text-base"
                      >
                        Verify now
                      </div>
                    }
                  />
                </div>
              );
            })()}
          </div>
        </div>

          {/* Your Minted Apartments Section - Figma: width: 401.5px, height: 265px */}
          <div className="flex flex-col space-y-4 flex-1 min-w-0">
            <div className="font-semibold text-base">Your Minted Apartments</div>
            <div className="relative bg-white border border-[#E3E3E3] rounded-[10px] p-4 shadow flex-1 min-h-[265px] overflow-auto">
            <div
              ref={ref_nfts}
              className="pb-1 flex gap-[8px] sm:gap-[10px] items-center overflow-x-auto scrollbar-hide w-full h-full"
            >
              {myNfts.map((nft, i) => {
                const validatedNft = validatePropertyData(nft);
                return (
                  <Fade key={i}>
                    <div className="w-max h-max relative group flex-shrink-0">
                      <RentalItem
                        showArrow={false}
                        token_id={validatedNft.token_id}
                        tokenData={validatedNft}
                        imageHeight={window.innerWidth < 640 ? 160 : 200}
                        onlyImages
                        hideWishlist={true}
                      />

                      <div className="group-hover:flex absolute top-0 left-0 w-full h-full hidden flex-col items-center justify-center">
                        <BlackButton
                          text="View"
                          onClick={() =>
                            navigate(
                              `/dashboard/host/nfts/${validatedNft.token_id}`
                            )
                          }
                        />
                      </div>
                    </div>
                  </Fade>
                );
              })}
            </div>

            {myNfts.length === 0 && (
              <div className="absolute inset-0 flex flex-col justify-center items-center text-center p-4">
                <div className="scale-75 sm:scale-100">
                  <EmptyProperty />
                </div>
                <div className="font-semibold text-sm sm:text-base mt-2">
                  You have no properties yet
                </div>
              </div>
            )}
          </div>
        </div>

          {/* Booking Request Section - Figma: width: 401.5px, height: 265px */}
   <div className="flex flex-col space-y-4 flex-1 min-w-0">
     <div className="font-semibold text-base">Booking request</div>
  <div
    ref={ref_requests}
    className="border border-[#E3E3E3] bg-white p-4 overflow-auto rounded-[10px] shadow flex-1 min-h-[265px] flex flex-col"
  >


<div className="w-full flex justify-center mb-4">
  <SelectionGroup
    defaultItem={flag}
    className="border border-[#e3e3e3] px-1 py-1 gap-1 flex items-center rounded-[10px] bg-white shadow w-full"
  >
    <SelectionItem
      SelectedItem={
        <div className="py-2 px-1 cursor-pointer bg-white rounded-[8px] flex-1 min-w-0 flex justify-center items-center gap-1 shadow">
          <div className="text-black font-semibold text-[10px] whitespace-nowrap">Upcoming</div>
          <div className="scale-75 flex-shrink-0"><LightIcon /></div>
        </div>
      }
      UnselectedItem={
        <div
          onClick={() => setFlag(0)}
          className="py-2 px-1 cursor-pointer hover:bg-[#f6f6f6] rounded-[8px] flex-1 min-w-0 flex justify-center items-center"
        >
          <div className="text-[#959595] text-[10px] whitespace-nowrap">Upcoming</div>
        </div>
      }
    />
    <SelectionItem
      SelectedItem={
        <div className="py-2 px-1 cursor-pointer bg-white rounded-[8px] flex-1 min-w-0 flex justify-center items-center gap-1 shadow">
          <div className="text-black font-semibold text-[10px] whitespace-nowrap">Current</div>
          {flag === 1 && <div className="scale-75 flex-shrink-0"><LightIcon /></div>}
        </div>
      }
      UnselectedItem={
        <div
          onClick={() => setFlag(1)}
          className="py-2 px-1 cursor-pointer hover:bg-[#f6f6f6] rounded-[8px] flex-1 min-w-0 flex justify-center items-center"
        >
          <div className="text-[#959595] text-[10px] whitespace-nowrap">Current</div>
        </div>
      }
    />
    <SelectionItem
      SelectedItem={
        <div className="py-2 px-1 cursor-pointer bg-white rounded-[8px] flex-1 min-w-0 flex justify-center items-center gap-1 shadow">
          <div className="text-black font-semibold text-[10px] whitespace-nowrap">Completed</div>
          {flag === 2 && <div className="scale-75 flex-shrink-0"><LightIcon /></div>}
        </div>
      }
      UnselectedItem={
        <div
          onClick={() => setFlag(2)}
          className="py-2 px-1 cursor-pointer hover:bg-[#f6f6f6] rounded-[8px] flex-1 min-w-0 flex justify-center items-center"
        >
          <div className="text-[#959595] text-[10px] whitespace-nowrap">Completed</div>
        </div>
      }
    />
    <SelectionItem
      SelectedItem={
        <div className="py-2 px-1 cursor-pointer bg-white rounded-[8px] flex-1 min-w-0 flex justify-center items-center gap-1 shadow">
          <div className="text-black font-semibold text-[10px] whitespace-nowrap">Cancelled</div>
          {flag === 3 && <div className="scale-75 flex-shrink-0"><LightIcon /></div>}
        </div>
      }
      UnselectedItem={
        <div
          onClick={() => setFlag(3)}
          className="py-2 px-1 cursor-pointer hover:bg-[#f6f6f6] rounded-[8px] flex-1 min-w-0 flex justify-center items-center"
        >
          <div className="text-[#959595] text-[10px] whitespace-nowrap">Cancelled</div>
        </div>
      }
    />
  </SelectionGroup>
</div>


            
            <div className="flex gap-[12px] sm:gap-[16px] items-center mt-[8px] sm:mt-[12px] pb-[8px] sm:pb-[12px] w-full overflow-x-auto scrollbar-hide">
              {isHydrating ? (
                <div className="flex flex-col items-center justify-center w-full h-full py-[20px] sm:py-[32px]">
                  <div className="text-sm sm:text-base">Loading reservations...</div>
                </div>
              ) : (
                (() => {
                  // 🚀 OPTIMIZED: Memoize the result to prevent re-computation on every render
                  const status = STATUS_MAP[flag] || "upcoming";
                  const matchingReservations =
                    backendReservationsByStatus?.[status] || [];
                  return matchingReservations.length ? (
                    matchingReservations.map(({ nft, reservation, key }, idx) => {
                      const period = reservation?.renting_period;
                      const start = Array.isArray(period) ? period[0] : 0;
                      const end = Array.isArray(period) ? period[1] : 0;
                      const validatedNft = validatePropertyData(nft);

                      return (
                        <Fade
                          key={
                            key ??
                            `${validatedNft.token_id}-${reservation?.address}-${start}-${end}-${idx}`
                          }
                        >
                          <div className="w-max h-max relative group flex-shrink-0">
                            <RentalItem
                              traveler={reservation?.address}
                              rentingPeriod={
                                Array.isArray(period) ? period : undefined
                              }
                              token_id={validatedNft.token_id}
                              tokenData={validatedNft}
                              onlyImages
                              imageHeight={window.innerWidth < 640 ? 120 : 153}
                              hideWishlist={true}
                            />
                            <div className="group-hover:flex absolute top-0 left-0 w-full h-full hidden flex-col items-center justify-center">
                              <BlackButton
                                text="View all"
                                onClick={() =>
                                  setPeriodParams(
                                    validatedNft.token_id,
                                    start,
                                    end
                                  )
                                }
                              />
                            </div>
                          </div>
                        </Fade>
                      );
                    })
                  ) : (
                    <div className="flex flex-col items-center w-full justify-center h-full py-[20px] sm:py-[32px]">
                      <div className="scale-75 sm:scale-100">
                        <EmptyBooking />
                      </div>
                      <div className="text-sm sm:text-base mt-[12px] sm:mt-[16px]">
                        No reservations yet
                      </div>
                      <div className="text-[#737373] text-xs sm:text-sm mt-[4px] sm:mt-[8px]">
                        {flag === 0 && "No upcoming reservations yet"}
                        {flag === 1 && "No current reservations yet"}
                        {flag === 2 && "No completed reservations yet"}
                        {flag === 3 && "No cancelled reservations yet"}
                      </div>
                    </div>
                  );
                })()
              )}
            </div>
          </div>
                </div>
        </div>

        {/* Desktop Bottom Section */}
        <div className="hidden lg:flex flex-row gap-6 w-full mt-6">
                     {/* Mint Your Real Estate Apartment Section - Figma: width: 542px, height: 400px */}
  <div className="flex flex-col space-y-4 flex-1 min-w-0">
    <div className="font-semibold text-base">Mint Your Real Estate Apartment</div>
    <div className="bg-white flex flex-col justify-around items-center border border-[#E3E3E3] rounded-[10px] p-6 shadow flex-1 min-h-[265px]">
              <div className="scale-75 sm:scale-100">
                <EmptyNft />
              </div>
              {myNfts.length === 0 && (
                <div className="flex flex-col items-center text-center">
                  <div className="font-semibold text-sm sm:text-base">No Apartments minted yet</div>
                  <div className="text-[#959595] font-normal text-[12px] sm:text-[14px]">
                    Your Apartment minted is empty yet
                  </div>
                </div>
              )}

              <div className="w-full flex flex-col gap-3">
                <PurpleButton
                  text={
                    <div
                      onClick={() => {
                        if (!checkVerificationAndRedirect("create property")) {
                          return;
                        }
                        navigate("/dashboard/host/mint");
                      }}
                      className="min-w-[100px] text-center text-sm sm:text-base"
                    >
                      Create New Property
                    </div>
                  }
                />
                <PurpleButton
                  text={
                    <div
                      onClick={() => {
                        if (!checkVerificationAndRedirect("import from Airbnb")) {
                          return;
                        }
                        setShowAirbnbModal(true);
                      }}
                      className="min-w-[100px] text-center text-sm sm:text-base"
                    >
                      Import from Airbnb
                    </div>
                  }
                />
                <BlackButton
                  text={
                    <div
                      onClick={() => {
                        if (!checkVerificationAndRedirect("view drafts")) {
                          return;
                        }
                        setShowDraftsModal(true);
                      }}
                      className="min-w-[100px] text-center text-sm sm:text-base"
                    >
                      View Drafts
                    </div>
                  }
                />
              </div>
            </div>
          </div>

                     {/* Notifications Section - Figma: width: 542px, height: 400px */}
            <div className="flex flex-col space-y-4 flex-1 min-w-0 max-h-[400px]">
    <div className="font-semibold text-base">Notifications</div>
             {notificationContent.length > 0 ? (
               <div className="bg-white border border-[#E3E3E3] rounded-[10px] p-4 shadow h-[400px] max-h-[400px] overflow-y-auto overflow-x-hidden">
                {notificationContent
                  .slice()
                  .reverse()
                  .map((item, i) => {
                    if (item.type === "event")
                      return (
                        <div
                          key={`event-${i}`}
                          onClick={() => {
                            // Remove notification when clicked
                            dispatch(removeNotificationById({
                              type: "event",
                              identifier: {
                                token_id: item.data.token_id,
                                sender: item.data.sender,
                                action: item.data.action,
                                txHash: item.data.txHash,
                              }
                            }));
                            const path = getNotificationPath(item, account, nfts);
                            navigate(path);
                          }}
                          className="hover:bg-[#f0f0f0] rounded-[4px] cursor-pointer p-[4px] sm:p-[6px] w-full flex items-center justify-between"
                        >
                          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                            <NotificationImageWithFallback
                              src={profiles[item.data.sender]?.Avatar}
                              alt="User Avatar"
                              className="shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] flex-shrink-0"
                              size="lg"
                            />

                            <div className="min-w-0 flex-1">
                              <div className="text-xs sm:text-base truncate">
                                {profiles[item.data.sender]?.Name
                                  ? profiles[item.data.sender]?.Name?.replace(
                                      "/",
                                      " "
                                    )
                                  : truncateWalletAddress(item.data.sender)}
                              </div>
                              <div className="flex gap-1 sm:gap-2 min-w-0 flex-1 truncate">
                                <span className="font-semibold text-xs sm:text-base">
                                  {parseAction(item.data.action)}
                                </span>
                                <span className="truncate text-xs sm:text-base">
                                  {nfts?.[item.data.token_id]?.metaData?.buildingName || ''}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
                            <PropertyImageWithFallback
                              src={nfts?.[item.data.token_id]?.metaData?.images?.[0]}
                              alt="Property"
                              className="w-[30px] sm:w-[60px] h-[20px] sm:h-full rounded-[4px]"
                            />
                            <div className="text-right">
                              <div className="text-[10px] sm:text-[12px]">
                                {formatDate(
                                  Math.round(new Date(item.data.timeUTC).getTime() / 1000),
                                  diffToUTC
                                )}
                              </div>
                              <div className="text-[10px] sm:text-[12px]">
                                {formatTime(
                                  Math.round(new Date(item.data.timeUTC).getTime() / 1000),
                                  diffToUTC
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    else
                      return (
                        <div
                          key={`message-${i}`}
                          onClick={async () => {
                            // Remove notification when clicked
                            dispatch(removeNotificationById({
                              type: "message",
                              identifier: {
                                chatId: item.data.id,
                              }
                            }));
                            await navigateToMessageNotificationInbox({
                              item,
                              account,
                              nfts,
                              pathname: location.pathname,
                              navigate,
                            });
                          }}
                          className="hover:bg-[#f0f0f0] rounded-[4px] cursor-pointer p-[4px] sm:p-[6px] w-full flex items-center justify-between"
                        >
                          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                            {profiles[item.data.message.sender]?.Avatar ? (
                              <img
                                alt=""
                                src={profiles[item.data.message.sender]?.Avatar}
                                className="w-[36px] h-[36px] sm:w-[44px] sm:h-[44px] rounded-full shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)] flex-shrink-0"
                              />
                            ) : (
                              <div className="w-[36px] h-[36px] sm:w-[44px] sm:h-[44px] flex bg-[#f0f0f0] rounded-full flex-shrink-0">
                                <div className="scale-75 sm:scale-100 m-auto">
                                  <UserBoxIcon />
                                </div>
                              </div>
                            )}

                            <div className="min-w-0 flex-1">
                              <div className="text-xs sm:text-base truncate">
                                {profiles[item.data.message.sender]?.Name
                                  ? profiles[
                                      item.data.message.sender
                                    ]?.Name?.replace("/", " ")
                                  : truncateWalletAddress(
                                      item.data.message.sender
                                    )}
                              </div>

                              <div className="flex gap-1 sm:gap-2 min-w-0 flex-1 truncate">
                                <span className="truncate text-xs sm:text-base">
                                  Sent new message
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
                            <PropertyImageWithFallback
                              src={nfts?.[item.data.nftId]?.metaData?.images?.[0]}
                              alt="Property"
                              className="w-[30px] sm:w-[60px] h-[20px] sm:h-full rounded-[4px]"
                            />
                            <div className="text-right">
                              <div className="text-[10px] sm:text-[12px]">
                                {formatDate(
                                  Math.round(new Date(item.data.message.generatedTime).getTime() / 1000),
                                  diffToUTC
                                )}
                              </div>
                              <div className="text-[10px] sm:text-[12px]">
                                {formatTime(
                                  Math.round(new Date(item.data.message.generatedTime).getTime() / 1000),
                                  diffToUTC
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                  })}
              </div>
            ) : (
              <div className="bg-white flex flex-col justify-around items-center border-[2px] h-[220px] sm:h-[300px] w-full border-[#E3E3E3] p-[16px] sm:p-[24px] rounded-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,255,0.8)]">
                <div className="scale-75 sm:scale-100">
                  <EmptyInbox />
                </div>
                <div className="flex flex-col items-center text-center">
                  <div className="font-semibold text-sm sm:text-base">No notifications yet</div>
                  <div className="text-[#959595] font-normal text-[12px] sm:text-[14px]">
                    Your inbox is empty yet
                  </div>
                </div>
                <PurpleButton
                  text={<div className="min-w-[100px] text-center text-sm sm:text-base">Inbox</div>}
                  onClick={() => navigate("/dashboard/host/inbox")}
                />
              </div>
            )}
          </div>
        </div>
        </div>
      </div>
      
      {/* Drafts Modal */}
      {showDraftsModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-[16px] w-full max-w-6xl max-h-[90vh] overflow-auto shadow-2xl">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-[#E3E3E3] p-6 flex justify-between items-center rounded-t-[16px] z-10">
              <h2 className="text-2xl font-semibold">Property Drafts</h2>
              <button
                onClick={() => setShowDraftsModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-6">
              <DraftsViewer 
                isModal={true}
                onClose={() => setShowDraftsModal(false)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Airbnb Import Modal */}
      <Modal
        open={showAirbnbModal}
        onClose={() => {
          if (!isImporting) {
            setShowAirbnbModal(false);
            setAirbnbUrl("");
          }
        }}
        center
        showCloseIcon={false}
        classNames={{
          modal: "airbnb-import-modal",
          overlay: "modal-overlay",
        }}
        styles={{
          modal: {
            borderRadius: "16px",
            padding: "0",
            maxWidth: "600px",
            width: "90%",
          },
        }}
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold">Import from Airbnb</h2>
            <button
              onClick={() => {
                if (!isImporting) {
                  setShowAirbnbModal(false);
                  setAirbnbUrl("");
                }
              }}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              disabled={isImporting}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Airbnb Listing URL
              </label>
              <input
                type="url"
                value={airbnbUrl}
                onChange={(e) => setAirbnbUrl(e.target.value)}
                placeholder="https://www.airbnb.com/rooms/12345678"
                className="w-full px-4 py-3 border border-[#E3E3E3] rounded-[12px] focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                disabled={isImporting}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !isImporting) {
                    handleImportFromAirbnb();
                  }
                }}
              />
              <p className="mt-2 text-sm text-gray-500">
                Paste your Airbnb listing URL above. We'll automatically extract all property details.
              </p>
            </div>

            {isImporting && (
              <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div>
                <span className="text-sm text-gray-700">Importing property data from Airbnb...</span>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <BlackButton
                text={
                  <div
                    onClick={() => {
                      if (!isImporting) {
                        setShowAirbnbModal(false);
                        setAirbnbUrl("");
                      }
                    }}
                    className="w-full text-center text-sm sm:text-base"
                  >
                    Cancel
                  </div>
                }
                disabled={isImporting}
              />
              <PurpleButton
                text={
                  <div
                    onClick={handleImportFromAirbnb}
                    className="w-full text-center text-sm sm:text-base"
                  >
                    {isImporting ? "Importing..." : "Import Property"}
                  </div>
                }
                disabled={isImporting || !airbnbUrl.trim()}
              />
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};